import { onDeactivated, onUnmounted } from 'vue'

const baseUrl = `${window.location.origin}/api2/`

export type Options = {
  abortController?: AbortController
  logoutOn401?: boolean
}
export type RequestOptions = Options & { timeoutInMs?: number }
export type HttpResponse<T> = { status: number; body?: T }
export type GetRequest = <T>(path: string, options?: Options) => Promise<HttpResponse<T>>
export type PostRequest = <T>(path: string, body?: object, options?: Options) => Promise<HttpResponse<T>>
export type DeleteRequest = (path: string, options?: Options) => Promise<HttpResponse<void>>
export type Api = ReturnType<typeof useApi>

let on401Handler: (() => Promise<void>) | undefined

/** Register the function to call when the api receives a 401. Each app wires its own policy
 * (e.g. clear session and redirect). Calls are no-ops until a handler is registered.
 * Throws if called more than once — silent overwrite would mask a wiring bug. */
export function setOn401Handler(handler: () => Promise<void>) {
  if (on401Handler) {
    throw new Error('on401 handler is already set')
  }
  on401Handler = handler
}

let onFailedResponseHandler: ((path: string, status: number) => void) | undefined

export function setOnFailedResponseHandler(handler: (path: string, status: number) => void) {
  if (onFailedResponseHandler) {
    throw new Error('failed response handler is already set')
  }
  onFailedResponseHandler = handler
}

/** Make a GET request to the API. Do not use this in Vue components, instead use {@link useApi}. Only
 * used in non-Vue components where we do not want to pass an instance of useApi. */
export function get<T>(path: string, options?: RequestOptions) {
  return request<T>('get', path, undefined, options)
}

/** Make a GET request returning the body. Do not use this in Vue components, instead use {@link useApi}. Only
 * used in non-Vue components where we do not want to pass an instance of useApi. */
export async function getBody<T>(path: string, options?: RequestOptions) {
  return (await get<T>(path, options)).body
}

/** Make a POST request to the API. Do not use this in Vue components, instead use {@link useApi}. Only
 * used in non-Vue components where we do not want to pass an instance of useApi. */
export function post<T>(path: string, body?: object, options?: RequestOptions) {
  return request<T>('post', path, body, options)
}

/** Make a POST request to the API, returning the body. Do not use this in Vue components, instead use {@link useApi}. Only
 * used in non-Vue components where we do not want to pass an instance of useApi. */
export async function postGetBody<T>(path: string, body?: object, options?: RequestOptions) {
  return (await post<T>(path, body, options)).body
}

/** Make a DELETE request to the API. Do not use this in Vue components, instead use {@link useApi}. Only
 * used in non-Vue components where we do not want to pass an instance of useApi. */
export function apiDelete(path: string, options?: RequestOptions) {
  return request<void>('delete', path, undefined, options)
}

async function request<T>(
  method: string,
  path: string,
  body?: object,
  options?: RequestOptions
): Promise<HttpResponse<T>> {
  const uri = `${baseUrl}${path}`
  const requestBody = body ? JSON.stringify(body) : undefined
  const headers = new Headers()
  headers.append('Content-Type', 'application/json')
  const abortController = options?.abortController ?? new AbortController()
  const requestInit: RequestInit = {
    method,
    body: requestBody,
    headers,
    signal: abortController.signal
  }
  const response = await fetchWithTimeout(uri, requestInit, abortController, options?.timeoutInMs)
  if (response.status === 401 && options?.logoutOn401 !== false) {
    await on401Handler?.()
  } else if (!response.ok && response.status !== 401) {
    onFailedResponseHandler?.(path, response.status)
  }
  const contentType = response.headers.get('content-type')
  try {
    const responseBody = await getResponseBody<T>(contentType, response)
    return { status: response.status, body: responseBody }
  } catch (e) {
    console.log(e)
    return { status: 500 }
  }
}

async function fetchWithTimeout(
  uri: string,
  requestInit: RequestInit,
  abortController: AbortController,
  timeoutInMs: number | undefined
) {
  const timeout = timeoutInMs ? setTimeout(() => abortController.abort(), timeoutInMs) : undefined
  try {
    return await fetch(uri, requestInit)
  } finally {
    clearTimeout(timeout)
  }
}

async function getResponseBody<T>(contentType: string | null, response: Response) {
  if (contentType?.includes('json')) {
    return (await response.json()) as T
  }
  if (
    contentType?.includes('image/png') ||
    contentType?.includes('text/csv') ||
    contentType?.includes('application/zip')
  ) {
    return (await response.blob()) as T
  }
  return undefined
}

async function requestWithTrackedAbort<T>(
  abortControllers: Set<AbortController>,
  method: string,
  path: string,
  body?: object,
  options?: Options
): Promise<HttpResponse<T>> {
  try {
    options ??= {}
    options.abortController ??= new AbortController()
    abortControllers.add(options.abortController)
    const response = await request<T>(method, path, body, options)
    // Remove from tracked once completed
    abortControllers.delete(options.abortController)
    return response
  } catch {
    // Should only be CanceledError from abort
    // 499 is non-standard but might be useful to indicate abort happened https://httpstatus.in/499/
    return { status: 499 }
  }
}

function setupAbortControllerTracking() {
  // Track all requests abortControllers to cancel onUnmounted/Deactivated
  const abortControllers = new Set<AbortController>()
  const abort = () => {
    abortControllers.forEach(x => x.abort())
    abortControllers.clear()
  }
  onUnmounted(() => abort())
  onDeactivated(() => abort())
  return abortControllers
}

/** Functions to allow accessing the API. This should be used in Vue components rather
 * than using the pure api functions because requests are tracked and automatically
 * aborted onUnmounted/Deactivated (eg user navigates away). */
export function useApi() {
  const abortControllers = setupAbortControllerTracking()

  const get = <T>(path: string, options?: Options) => requestWithTrackedAbort<T>(abortControllers, 'get', path, options)
  const getBody = async <T>(path: string, options?: Options) => {
    const response = await get<T>(path, options)
    return response.body
  }
  const post = <T>(path: string, body?: object, options?: Options) =>
    requestWithTrackedAbort<T>(abortControllers, 'post', path, body, options)
  const postGetBody = async <T>(path: string, body?: object, options?: Options) => {
    const response = await post<T>(path, body, options)
    return response.body
  }
  const put = <T>(path: string, body?: object, options?: Options) =>
    requestWithTrackedAbort<T>(abortControllers, 'put', path, body, options)

  const deleteRequest = (path: string, options?: Options) =>
    requestWithTrackedAbort<void>(abortControllers, 'delete', path, options)

  return {
    /** Make a GET request to the API. Requests are aborted onUnmounted/Deactivated. */
    get,
    /** Make a GET request to the API, returning the body. Requests are aborted onUnmounted/Deactivated. */
    getBody,
    /** Make a POST request to the API. Requests are aborted onUnmounted/Deactivated. */
    post,
    /** Make a POST request to the API, returning the body. Requests are aborted onUnmounted/Deactivated. */
    postGetBody,
    put,
    /** Make a DELETE request to the API. Requests are aborted onUnmounted/Deactivated. */
    delete: deleteRequest
  }
}

/** Functions to allow accessing the API. If called more than once, the previous request
 * will be aborted. Useful for forms that are repeatedly submitted, eg run list filters.
 * If you are making two parallel requests, use two instances of this, otherwise one of the
 * calls will be aborted. Also automatically aborts requests onUnmounted/Deactivated.*/
export function useApiWithAbortPrevious(): Api {
  const abortControllers = setupAbortControllerTracking()
  let abortController: AbortController
  const abortPreviousRequest = () => {
    if (abortController) abortController.abort()
    abortController = new AbortController()
  }

  const get = <T>(path: string, options?: Options) => {
    abortPreviousRequest()
    return requestWithTrackedAbort<T>(abortControllers, 'get', path, {
      ...options,
      abortController
    })
  }
  const getBody = async <T>(path: string, options?: Options) => {
    const response = await get<T>(path, options)
    return response.body
  }
  const post = <T>(path: string, body?: object, options?: Options) => {
    abortPreviousRequest()
    return requestWithTrackedAbort<T>(abortControllers, 'post', path, body, {
      ...options,
      abortController
    })
  }
  const postGetBody = async <T>(path: string, body?: object, options?: Options) => {
    const response = await post<T>(path, body, options)
    return response.body
  }
  const put = <T>(path: string, body?: object, options?: Options) => {
    abortPreviousRequest()
    return requestWithTrackedAbort<T>(abortControllers, 'put', path, body, {
      ...options,
      abortController
    })
  }
  const deleteRequest = (path: string, options?: Options) => {
    abortPreviousRequest()
    return requestWithTrackedAbort<void>(abortControllers, 'delete', path, {
      ...options,
      abortController
    })
  }

  return {
    /** Make a GET request to the API. If called more than once, the previous request will
     * be aborted. Requests are also aborted onUnmounted/Deactivated. */
    get,
    /** Make a GET request to the API, returning the body. If called more than once, the previous request will
     * be aborted. Requests are also aborted onUnmounted/Deactivated. */
    getBody,
    /** Make a POST request to the API. If called more than once, the previous request will
     * be aborted. Requests are also aborted onUnmounted/Deactivated. */
    post,
    /** Make a POST request to the API, returning the body. If called more than once, the previous request will
     * be aborted. Requests are also aborted onUnmounted/Deactivated. */
    postGetBody,
    put,
    /** Make a DELETE request to the API. If called more than once, the previous request will
     * be aborted. Requests are also aborted onUnmounted/Deactivated. */
    delete: deleteRequest
  }
}

export type ProblemDetailsResponse = {
  title: string
}
